import { useEffect, useMemo, useRef, useState } from "react";
import {
  catalogoExportaciones,
  type ClienteNexo,
  type DatasetExportacion,
  descargarExportacion,
  type FilaEntidad,
  type ResultadoEntidades,
  type StatsNexo,
  vizEntidades,
  vizBuscarSujetosRegistro,
  vizBuscarParecidos,
  type NombreParecido,
  type SujetoBusqueda,
  vizStats,
} from "../api.ts";
import { armarCsv, descargarCsv } from "../csv.ts";
import { C, entero, fecha, programaVista, schemaEs, st } from "../theme.ts";
import { Cargando, ChipPrograma, ChipSchema, ErrorBox, celdaTd, celdaTh } from "../ui/componentes.tsx";
import { DistintivoRegistro, textoDistintivo } from "../ui/RedRegistro.tsx";
import { rutaAHash } from "../rutas.ts";
import { detalleDeParecido, fichaDeParecido, pedirParecidos } from "../parecidos.ts";
import { destinoDeFila, esPosible, MARCA_POSIBLE, MARCA_POSIBLE_AYUDA, relacionesQueNoCaben, tonoDeFila } from "../ficha/buscador.ts";
import { RELACIONES_FILA, SAT_REP, texto } from "../../../shared/rotulos/index.ts";
import "../ficha/ficha.css";

const TAM_PAGINA = 50;
const TOPE_EXPORT = 5000;
const DEBOUNCE_MS = 350;

interface Filtros {
  q: string;
  schema: string;
  pais: string;
  programa: string;
  fuente: string;
  orden: string;
  /** Registro de comercio: "" todas · "red" con red · "posible" sin confirmar · "no" sin coincidencia en la corrida vigente */
  registro: "" | "red" | "posible" | "no";
}

const FILTROS_VACIOS: Filtros = { q: "", schema: "", pais: "", programa: "", fuente: "", orden: "nombre", registro: "" };


/**
 * Explorador — la tabla dinámica del dataset: búsqueda por nombre/alias
 * (contención sobre el nombre normalizado), filtros por tipo/país/programa/
 * fuente, orden, paginación y export CSV de TODO lo filtrado (tope 5,000).
 */
export function Explorador({ cliente }: { cliente: ClienteNexo }) {
  const [filtros, setFiltros] = useState<Filtros>(FILTROS_VACIOS);
  const [pagina, setPagina] = useState(0);
  const [resultado, setResultado] = useState<ResultadoEntidades | null>(null);
  const [cargando, setCargando] = useState(true);
  const [exportando, setExportando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<StatsNexo | null>(null);
  const [datasets, setDatasets] = useState<DatasetExportacion[]>([]);
  const [descargando, setDescargando] = useState<string | null>(null);
  const [bytesDescargados, setBytesDescargados] = useState(0);
  const [descargaLista, setDescargaLista] = useState<string | null>(null);
  // Última petición lanzada: descarta respuestas viejas que lleguen tarde.
  const reqSeq = useRef(0);
  // Personas y empresas del registro de comercio con relación con OFAC: la búsqueda común de Nexo y Argus Team (2026-10-01).
  const [siger, setSiger] = useState<SujetoBusqueda[]>([]);
  const [sigerTotal, setSigerTotal] = useState(0);
  // La consulta cuya respuesta del registro ya llegó sin error, y la de la tabla de la lista (para los nombres parecidos).
  const [sigerDe, setSigerDe] = useState<string | null>(null);
  const [resultadoDe, setResultadoDe] = useState<string | null>(null);
  // 2026-10-02: nombres parecidos cuando la búsqueda por nombre no encontró a nadie.
  const [parecidos, setParecidos] = useState<{ q: string; filas: NombreParecido[] } | null>(null);
  useEffect(() => {
    const q = filtros.q.trim();
    setSigerDe(null);
    if (q.length < 3) {
      setSiger([]);
      setSigerTotal(0);
      return;
    }
    let vivo = true;
    const timer = setTimeout(() => {
      vizBuscarSujetosRegistro(cliente, q)
        .then((r) => { if (vivo) { setSiger(r.filas); setSigerTotal(r.total); setSigerDe(q); } })
        .catch(() => { if (vivo) { setSiger([]); setSigerTotal(0); } });
    }, DEBOUNCE_MS);
    return () => {
      vivo = false;
      clearTimeout(timer);
    };
  }, [cliente, filtros.q]);

  useEffect(() => {
    vizStats(cliente).then(setStats).catch(() => {
      /* los selects de filtro degradan a texto libre sin stats */
    });
  }, [cliente]);

  useEffect(() => {
    catalogoExportaciones(cliente).then((c) => { setDatasets(c.datasets); }).catch(() => {
      /* la tabla sigue disponible si la Edge Function aún no está desplegada */
    });
  }, [cliente]);

  useEffect(() => {
    const seq = ++reqSeq.current;
    setCargando(true);
    setError(null);
    const timer = setTimeout(() => {
      vizEntidades(cliente, {
        q: filtros.q.trim() || null,
        schema: filtros.schema || null,
        pais: filtros.pais || null,
        programa: filtros.programa || null,
        fuente: filtros.fuente || null,
        limit: TAM_PAGINA,
        offset: pagina * TAM_PAGINA,
        orden: filtros.orden,
        registro: filtros.registro || null,
      })
        .then((r) => {
          if (reqSeq.current === seq) {
            setResultado(r);
            setResultadoDe(filtros.q.trim());
            setCargando(false);
          }
        })
        .catch((e: unknown) => {
          if (reqSeq.current === seq) {
            setError(e instanceof Error ? e.message : String(e));
            setCargando(false);
          }
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [cliente, filtros, pagina]);

  const qActual = filtros.q.trim();
  const buscarParecidos = pedirParecidos({
    q: qActual,
    otrosFiltros: Boolean(filtros.schema || filtros.pais || filtros.programa || filtros.fuente || filtros.registro),
    pagina,
    listaDe: cargando ? null : resultadoDe,
    listaTotal: resultado?.total ?? null,
    registroDe: sigerDe,
    registroTotal: sigerTotal,
  });
  useEffect(() => {
    if (!buscarParecidos) {
      setParecidos(null);
      return;
    }
    let vivo = true;
    void vizBuscarParecidos(cliente, qActual).then((filas) => { if (vivo) setParecidos({ q: qActual, filas }); });
    return () => {
      vivo = false;
    };
  }, [cliente, buscarParecidos, qActual]);

  function cambiar<K extends keyof Filtros>(k: K, v: Filtros[K]) {
    setFiltros((f) => ({ ...f, [k]: v }));
    setPagina(0);
  }

  async function exportarCsv() {
    setExportando(true);
    setError(null);
    try {
      const r = await vizEntidades(cliente, {
        q: filtros.q.trim() || null,
        schema: filtros.schema || null,
        pais: filtros.pais || null,
        programa: filtros.programa || null,
        fuente: filtros.fuente || null,
        limit: TOPE_EXPORT,
        offset: 0,
        orden: filtros.orden,
        registro: filtros.registro || null,
      });
      const csv = armarCsv(
        ["nombre", "tipo", "pais", "programas", "fuentes", "relaciones", "listado_desde", "registro_comercio", "registro_liga_principal", "id"],
        r.filas.map((f: FilaEntidad) => [
          f.caption,
          schemaEs(f.schema),
          f.pais,
          f.programas.map((p) => programaVista(p).etiqueta),
          f.fuentes,
          f.n_aristas,
          f.listing_desde,
          f.registro ? textoDistintivo(f.registro) : "",
          f.registro?.frase ?? f.registro?.recomendacion ?? "",
          f.id,
        ]),
      );
      const hoy = new Date().toISOString().slice(0, 10);
      descargarCsv(`argus-nexo-entidades-${hoy}.csv`, csv);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setExportando(false);
    }
  }

  async function descargarCompleto(dataset: DatasetExportacion) {
    setDescargando(dataset.id);
    setBytesDescargados(0);
    setDescargaLista(null);
    setError(null);
    try {
      await descargarExportacion(cliente, dataset, setBytesDescargados);
      setDescargaLista(dataset.archivo);
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDescargando(null);
    }
  }

  const totalPaginas = useMemo(
    () => (resultado ? Math.max(1, Math.ceil(resultado.total / TAM_PAGINA)) : 1),
    [resultado],
  );

  const selectSt = { ...st.input, width: "auto", minWidth: 140 };

  return (
    <div>
      <h1 style={{ ...st.display, fontSize: 26, margin: "4px 0 14px" }}>Explorador</h1>

      {datasets.length > 0 ? (
        <div style={{ ...st.tarjeta, marginBottom: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Descargas canónicas completas</div>
          <div style={{ color: C.muted, fontSize: 13, marginBottom: 10 }}>
            Datos vigentes e históricos desde las tablas canónicas. No dependen del staging ni tienen límite de 5,000 filas.
          </div>
          {descargando ? (
            <div style={{ color: C.muted, fontSize: 13, marginBottom: 10 }} role="status">
              Preparando archivo ({(bytesDescargados / 1_048_576).toFixed(1)} MB). La descarga comenzará automáticamente cuando esté completo.
            </div>
          ) : descargaLista ? (
            <div style={{ color: C.green, fontSize: 13, marginBottom: 10 }} role="status">
              Archivo listo: {descargaLista}
            </div>
          ) : null}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {datasets.map((d) => (
              <button
                key={d.id}
                style={st.btn}
                disabled={descargando !== null}
                title={d.descripcion}
                onClick={() => { void descargarCompleto(d); }}
              >
                {descargando === d.id ? "Descargando…" : d.titulo}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div style={{ ...st.tarjeta, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ flex: "2 1 260px" }}>
          <label style={st.label} htmlFor="q">Nombre o alias</label>
          <input
            id="q"
            style={{ ...st.input, width: "100%" }}
            placeholder="ej. Juraidini, La Peseta, CJNG… (palabras en cualquier orden)"
            value={filtros.q}
            onChange={(e) => {
              cambiar("q", e.target.value);
            }}
          />
        </div>
        <div>
          <label style={st.label} htmlFor="f-tipo">Tipo</label>
          <select id="f-tipo" style={selectSt} value={filtros.schema} onChange={(e) => { cambiar("schema", e.target.value); }}>
            <option value="">Todos</option>
            {(stats?.por_schema ?? []).map((s) => (
              <option key={s.schema} value={s.schema}>{schemaEs(s.schema)}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={st.label} htmlFor="f-pais">País</label>
          <select id="f-pais" style={selectSt} value={filtros.pais} onChange={(e) => { cambiar("pais", e.target.value); }}>
            <option value="">Todos</option>
            {(stats?.por_pais ?? []).filter((p) => p.pais !== "sin país").map((p) => (
              <option key={p.pais} value={p.pais}>{p.pais}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={st.label} htmlFor="f-prog">Programa</label>
          <select id="f-prog" style={selectSt} value={filtros.programa} onChange={(e) => { cambiar("programa", e.target.value); }}>
            <option value="">Todos</option>
            {(stats?.por_programa ?? []).map((p) => (
              <option key={p.programa} value={p.programa}>{programaVista(p.programa).etiqueta}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={st.label} htmlFor="f-fuente">Fuente</label>
          <select id="f-fuente" style={selectSt} value={filtros.fuente} onChange={(e) => { cambiar("fuente", e.target.value); }}>
            <option value="">Todas</option>
            {(stats?.fuentes ?? []).map((f) => (
              <option key={f.id} value={f.id}>{f.nombre}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={st.label} htmlFor="f-registro">Registro Público de Comercio</label>
          <select id="f-registro" style={selectSt} value={filtros.registro} onChange={(e) => { cambiar("registro", e.target.value as Filtros["registro"]); }}>
            <option value="">Todos</option>
            <option value="red">Con red</option>
            <option value="posible">Posible (sin confirmar)</option>
            <option value="no">Sin coincidencia en la lectura vigente</option>
          </select>
        </div>
        <div>
          <label style={st.label} htmlFor="f-orden">Orden</label>
          <select id="f-orden" style={selectSt} value={filtros.orden} onChange={(e) => { cambiar("orden", e.target.value); }}>
            <option value="nombre">Nombre</option>
            <option value="aristas">Más relaciones</option>
            <option value="pais">País</option>
            <option value="schema">Tipo</option>
          </select>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            style={st.btn}
            onClick={() => {
              setFiltros(FILTROS_VACIOS);
              setPagina(0);
            }}
          >
            Limpiar
          </button>
          <button style={st.btnPrim} onClick={() => { void exportarCsv(); }} disabled={exportando}>
            {exportando ? "Exportando…" : "Exportar búsqueda (máx. 5,000)"}
          </button>
        </div>
      </div>

      {error ? <ErrorBox mensaje={error} /> : null}

      {/* Región viva que existe antes de la respuesta: lo que aparece dentro (los nombres parecidos) se anuncia. */}
      <div role="status">
      {parecidos && parecidos.q === qActual && parecidos.filas.length > 0 ? (
        <div style={{ ...st.tarjeta, marginBottom: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{`No encontramos «${qActual}».`}</div>
          <div style={{ color: C.muted, fontSize: 12.5, marginBottom: 8 }}>Nombres parecidos:</div>
          <ul aria-label="Nombres parecidos" style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {parecidos.filas.map((p) => {
              const ficha = fichaDeParecido(p);
              return (
                <li key={`${p.kind}:${p.id}:${p.runId ?? ""}`} style={{ padding: "5px 0", borderTop: `1px solid ${C.line}` }}>
                  {ficha ? (
                    <a href={rutaAHash(ficha)} style={{ color: C.ink, fontWeight: 600, textDecoration: "none" }}>
                      {p.caption}
                    </a>
                  ) : (
                    <span style={{ fontWeight: 600 }}>{p.caption}</span>
                  )}
                  <div style={{ color: C.muted, fontSize: 12 }}>{detalleDeParecido(p)}</div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      </div>

      {siger.length > 0 ? (
        <div style={{ ...st.tarjeta, marginBottom: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>En el Registro Público de Comercio ({String(sigerTotal)})</div>
          <div style={{ color: C.muted, fontSize: 12.5, marginBottom: 8 }}>
            Personas y empresas del Registro Público de Comercio que coinciden con alguien de la lista de OFAC o se relacionan con
            un sancionado. Van de la relación más cercana a la más lejana, con la misma regla que el buscador de Argus Team. Puede
            ser otra persona con el mismo nombre: la ficha muestra la identidad y la cita del acta.
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {siger.map((a) => {
                const links = a.siger?.links ?? [];
                // Etapa 4: el nombre abre la ficha de esta persona o empresa (nexo.hallazgos en su corrida). Ya no abre la de una
                // identidad `alta` sin corroborar como si fuera la misma persona (antes, Explorador.tsx:383).
                const destino = destinoDeFila(a);
                // El tono lo manda la base: rojo sólo con identidad o relación corroboradas (lote 0b).
                const tonoFila = tonoDeFila(a);
                const tono = tonoFila === "rojo" ? C.red : tonoFila === "ambar" ? C.amber : C.muted;
                const expedientes = a.siger?.expedientes ?? 0;
                return (
                  <tr key={`${a.kind}:${a.id}@${a.runId ?? ""}`}>
                    <td style={{ ...celdaTd, fontWeight: 600, verticalAlign: "top" }}>
                      {destino ? (
                        <a href={rutaAHash(destino)} style={{ color: C.ink, textDecoration: "none" }}>
                          {a.caption}
                        </a>
                      ) : (
                        a.caption
                      )}
                      <div style={{ color: C.muted, fontWeight: 400, fontSize: 12 }}>
                        {a.kind === "sat_rep" ? `persona · ${SAT_REP.fila}`
                          : `${a.schema === "Company" ? "empresa" : "persona"} · documentos de ${String(expedientes)} ${expedientes === 1 ? "empresa" : "empresas"}`}
                        {a.group && a.group.count > 1 ? ` · ${String(a.group.count)} personas encontradas con este nombre` : ""}
                      </div>
                    </td>
                    <td style={{ ...celdaTd, verticalAlign: "top" }}>
                      {a.relacion ? (
                        <span style={{ fontSize: 11, fontWeight: 700, color: tono, border: `1px solid ${tono}`, borderRadius: 999, padding: "1px 8px" }}>
                          {a.relacion.texto}
                        </span>
                      ) : null}
                      {a.relacion?.posible ? <span className="nx-posible" title={MARCA_POSIBLE_AYUDA}>{MARCA_POSIBLE}</span> : null}
                      {a.relacion?.con ? <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{a.relacion.con}</div> : null}
                    </td>
                    <td style={{ ...celdaTd, fontSize: 12.5, verticalAlign: "top" }}>
                      {/* Corte 2c de los certificados del SAT: con la lista de la base, todas las relaciones (también la del certificado y
                          las que ya terminaron); sin ella (base anterior), los vínculos de siempre. */}
                      {a.relaciones ? (
                        <>
                          {a.relaciones.items.map((r, n) => {
                            const contenido = (
                              <>
                                <span style={{ color: C.muted }}>{r.texto}: </span>{r.con ?? ""}
                                {r.posible ? <span className="nx-posible" title={MARCA_POSIBLE_AYUDA}>{MARCA_POSIBLE}</span> : null}
                                {r.fuente === "acta" && r.vigente === false ? <span style={{ color: C.muted }}> · {RELACIONES_FILA.terminada}</span> : null}
                                {r.fuente === "certificado_sat" && r.anio ? <span style={{ color: C.muted }}> · {texto(RELACIONES_FILA.certificado, { anio: r.anio })}</span> : null}
                              </>
                            );
                            return r.entidad ? (
                              <a key={`${r.clave}-${r.entidad}-${String(n)}`} href={rutaAHash(a.kind === "sat_rep" ? { vista: "entidad", id: r.entidad } : { vista: "entidad", id: r.entidad, actor: a.id })}
                                style={{ display: "block", color: C.tiel, textDecoration: "none" }}
                                title="Abrir la ficha del sancionado con esta persona encontrada seleccionada">{contenido} →</a>
                            ) : <div key={`${r.clave}-${String(n)}`}>{contenido}</div>;
                          })}
                          {relacionesQueNoCaben(a) > 0 ? <div style={{ color: C.muted }}>{texto(RELACIONES_FILA.mas, { n: relacionesQueNoCaben(a) })}</div> : null}
                        </>
                      ) : links.map((l) => (
                        <a
                          key={l.entityId}
                          href={rutaAHash(a.kind === "sat_rep" ? { vista: "entidad", id: l.entityId } : { vista: "entidad", id: l.entityId, actor: a.id })}
                          style={{ display: "block", color: C.tiel, textDecoration: "none" }}
                          title="Abrir la ficha del sancionado con esta persona encontrada seleccionada"
                        >
                          {l.tipoTexto ? <span style={{ color: C.muted }}>{l.tipoTexto}: </span> : null}{l.caption}
                          {esPosible(l) ? <span className="nx-posible" title={MARCA_POSIBLE_AYUDA}>{MARCA_POSIBLE}</span> : null} →
                        </a>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {sigerTotal > siger.length ? (
            <div style={{ color: C.muted, fontSize: 12, marginTop: 6 }}>{`Se muestran ${String(siger.length)} de ${String(sigerTotal)}; escribe un nombre más completo para acotar.`}</div>
          ) : null}
        </div>
      ) : null}

      <div style={{ ...st.tarjeta, padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={celdaTh}>Nombre</th>
                <th style={celdaTh}>Tipo</th>
                <th style={celdaTh}>País</th>
                <th style={celdaTh}>Programas</th>
                <th style={{ ...celdaTh, textAlign: "right" }}>Relaciones</th>
                <th style={celdaTh}>Fecha en la lista</th>
              </tr>
            </thead>
            <tbody>
              {cargando ? (
                <tr><td colSpan={6}><Cargando texto="Consultando el grafo…" /></td></tr>
              ) : (resultado?.filas ?? []).length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: 30, textAlign: "center", color: C.muted, fontSize: 13.5 }}>
                    Sin resultados con estos filtros.
                  </td>
                </tr>
              ) : (
                (resultado?.filas ?? []).map((f) => (
                  <tr
                    key={f.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      window.location.hash = rutaAHash({ vista: "entidad", id: f.id });
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = C.tielSoft; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
                  >
                    <td style={{ ...celdaTd, fontWeight: 600, maxWidth: 380 }}>
                      {f.caption ?? "—"}
                      {f.registro ? (
                        <span style={{ marginLeft: 8 }}><DistintivoRegistro resumen={f.registro} /></span>
                      ) : null}
                    </td>
                    <td style={{ ...celdaTd, whiteSpace: "nowrap" }}><ChipSchema schema={f.schema} /></td>
                    <td style={celdaTd}>{f.pais ?? "—"}</td>
                    <td style={{ ...celdaTd, maxWidth: 300 }}>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {f.programas.slice(0, 3).map((p) => <ChipPrograma key={p} programa={p} />)}
                        {f.programas.length > 3 ? (
                          <span style={{ fontSize: 11, color: C.muted, alignSelf: "center" }}>
                            +{String(f.programas.length - 3)}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td style={{ ...celdaTd, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {f.n_aristas > 0 ? entero(f.n_aristas) : "—"}
                    </td>
                    <td style={{ ...celdaTd, whiteSpace: "nowrap" }}>{fecha(f.listing_desde)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 14px",
            borderTop: `1px solid ${C.line}`,
            fontSize: 13,
            color: C.muted,
          }}
        >
          <span>
            {resultado ? `${entero(resultado.total)} entidades · página ${String(pagina + 1)} de ${String(totalPaginas)}` : "…"}
          </span>
          <span style={{ display: "flex", gap: 8 }}>
            <button
              style={{ ...st.btn, opacity: pagina === 0 ? 0.5 : 1 }}
              disabled={pagina === 0}
              onClick={() => { setPagina((p) => Math.max(0, p - 1)); }}
            >
              ← Anterior
            </button>
            <button
              style={{ ...st.btn, opacity: pagina + 1 >= totalPaginas ? 0.5 : 1 }}
              disabled={pagina + 1 >= totalPaginas}
              onClick={() => { setPagina((p) => p + 1); }}
            >
              Siguiente →
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}
